/**
 * useClientPoints — tiny hook that keeps the signed-in client's Crown
 * points balance in sync. Extracted Feb 24 2026 so multiple header
 * elements can share one source of truth without stacking a duplicate
 * pill (Karine ask: "don't need 2 pills that serve the same purpose").
 *
 * Returns { points, credit } or nulls when not signed in / no data yet.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useClientPoints() {
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setToken(localStorage.getItem("client_token") || null);
    const sync = () => setToken(localStorage.getItem("client_token") || null);
    window.addEventListener("storage", sync);
    window.addEventListener("kl-auth-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("kl-auth-change", sync);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) { setData(null); return; }
      try {
        const r = await axios.get(`${API}/client/points-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } catch {
        // Silently fail — a single 401 shouldn't punt the session.
        if (!cancelled) setData(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  if (!token || !data) return { points: null, credit: null, nextTier: null, pointsToNext: null, bestTierNow: null };
  return {
    points: Number(data?.points_available ?? 0),
    credit: Number(data?.current_credit_value ?? 0),
    // Feb 24 2026 — expose the next-tier ladder step so the header pill
    // can render a friendly "X pts to $Y credit" tooltip on hover.
    nextTier: data?.next_tier || null,        // { points, value, label } | null when at top
    pointsToNext: Number(data?.points_to_next_tier ?? 0),
    // Feb 24 2026 — best tier the client can currently redeem. Used by
    // the header pill to detect milestone crossings (once-per-tier gold
    // pulse the first time the client's balance unlocks a new tier).
    bestTierNow: data?.best_tier_now || null, // { points, value, label } | null when below the first tier
  };
}
